/**
 * Everything that converts evolution into how hard the biters actually hit.
 *
 *   maps/biter_battles_v2/biter_raffle.lua — unit tier weights
 *   maps/biter_battles_v2/feeding.lua      — damage / attack-speed modifiers, group size
 *   maps/biter_battles_v2/ai.lua           — wave count, threat refund factors
 */

import { BB_CONFIG, DEFAULTS } from './constants';
import { biterHealthFactor, luaRound } from './feeding';

export type UnitTier = 'small' | 'medium' | 'big' | 'behemoth';
export const UNIT_TIERS: UnitTier[] = ['small', 'medium', 'big', 'behemoth'];

export type TierWeights = Record<UnitTier, number>;

/**
 * `get_raffle_table` — biter_raffle.lua:27. Indexed by `level = floor(evo * 1000)`
 * using the *uncapped* evolution, which is why the mix keeps shifting past 100%.
 */
export function raffleWeights(evo: number): TierWeights {
  return raffleWeightsForLevel(Math.floor(evo * 1000));
}

/** Raw level-indexed form, matching `_test_get_raffle_table`. */
export function raffleWeightsForLevel(level: number): TierWeights {
  if (level < 500) {
    return {
      small: 1000 - level * 1.75,
      medium: Math.max(-250 + level * 1.5, 0),
      big: 0,
      behemoth: 0,
    };
  }
  if (level < 900) {
    return {
      small: Math.max(1000 - level * 1.75, 0),
      medium: 1000 - level,
      big: (level - 500) * 2,
      behemoth: 0,
    };
  }
  return {
    small: 0,
    medium: Math.max(1000 - level, 0),
    big: (level - 500) * 2,
    behemoth: (level - 900) * 8,
  };
}

/** Raffle weights normalised into probabilities. */
export function unitComposition(evo: number): TierWeights {
  const weights = raffleWeights(evo);
  const total = UNIT_TIERS.reduce((sum, tier) => sum + weights[tier], 0);
  if (total <= 0) return { small: 1, medium: 0, big: 0, behemoth: 0 };
  return {
    small: weights.small / total,
    medium: weights.medium / total,
    big: weights.big / total,
    behemoth: weights.behemoth / total,
  };
}

/**
 * `get_worm_raffle_table` — biter_raffle.lua:74. Worms are placed at map
 * generation from a distance-derived pseudo-evolution, *not* from game
 * evolution; included here for completeness.
 */
export function wormRaffleWeights(pseudoEvo: number): TierWeights {
  const level = Math.floor(pseudoEvo * 1000);
  const weights: TierWeights = {
    small: 1000 - level * 1.75,
    medium: level,
    big: 0,
    behemoth: 0,
  };
  if (level > 500) {
    weights.medium = 500 - (level - 500);
    weights.big = (level - 500) * 2;
  }
  if (level > 900) weights.behemoth = (level - 900) * 3;
  for (const tier of UNIT_TIERS) weights[tier] = Math.max(weights[tier], 0);
  return weights;
}

export interface StrengthModifiers {
  /** Effective max-HP multiplier versus the vanilla prototype. */
  health: number;
  /** Damage multiplier: 1.0 means unmodified. */
  damage: number;
  /** Attack-speed (gun speed) multiplier: 1.0 means unmodified. */
  speed: number;
}

export type UnitClass = 'normal' | 'boss';

/**
 * Normal biters — `set_biter_endgame_modifiers`, feeding.lua:33.
 * The damage modifier is only written once the engine evolution factor hits 1,
 * i.e. once uncapped evolution reaches 100%. There is no attack-speed modifier.
 */
export function normalBiterModifiers(
  evo: number,
  maxReanimThresh = DEFAULTS.maxReanimThresh,
): StrengthModifiers {
  const damageModifier = evo >= 1 ? luaRound(evo - 1, 3) : 0;
  return {
    health: biterHealthFactor(evo, maxReanimThresh),
    damage: 1 + damageModifier,
    speed: 1,
  };
}

/**
 * Boss biters — `update_boss_modifiers`, feeding.lua:19, called with
 * `damage_mod_mult = 2` and `speed_mod_mult = 1` whenever evo > 1.
 * Boss HP is `20 * biter_health_factor` (ai.lua:146).
 */
export function bossBiterModifiers(
  evo: number,
  maxReanimThresh = DEFAULTS.maxReanimThresh,
): StrengthModifiers {
  const active = evo > 1;
  return {
    health: 20 * biterHealthFactor(evo, maxReanimThresh),
    damage: active ? 1 + luaRound(evo, 3) * 2 : 1,
    speed: active ? 1 + luaRound(evo * 0.25, 3) : 1,
  };
}

export function strengthModifiers(
  evo: number,
  unitClass: UnitClass,
  maxReanimThresh = DEFAULTS.maxReanimThresh,
): StrengthModifiers {
  return unitClass === 'boss'
    ? bossBiterModifiers(evo, maxReanimThresh)
    : normalBiterModifiers(evo, maxReanimThresh);
}

/** `storage.max_group_size` step function — feeding.lua:258. */
export function maxGroupSize(evo: number): number {
  if (evo > 3.3) return 50;
  if (evo > 2.3) return 75;
  if (evo > 1.3) return 100;
  if (evo > 0.7) return 200;
  return DEFAULTS.maxGroupSizeInitial;
}

/** `math.ceil((max_group_size_initial - max_group_size) / 20)` — ai.lua:263. */
export function bossSpawnAttempts(evo: number): number {
  const size = maxGroupSize(evo);
  if (size === DEFAULTS.maxGroupSizeInitial) return 0;
  return Math.ceil((DEFAULTS.maxGroupSizeInitial - size) / 20);
}

/** Threat refund multiplier when a spawner dies — ai.lua:509. Uses clamped evo. */
export function spawnerThreatFactor(evo: number): number {
  return 1 + 9 * Math.min(evo, 1) ** 2.25;
}

/** Threat refund multiplier when a boss dies — ai.lua:507. */
export function bossThreatFactor(evo: number, maxReanimThresh = DEFAULTS.maxReanimThresh): number {
  return BB_CONFIG.healthMultiplierBoss * biterHealthFactor(evo, maxReanimThresh);
}

/** `ceil(get_threat_ratio(force) * 7)` — ai.lua:397. */
export function waveGroupCount(ownThreat: number, enemyThreat: number): number {
  if (ownThreat <= 0) return 0;
  const a = Math.max(ownThreat, 0);
  const b = Math.max(enemyThreat, 0);
  const total = a + b;
  const ratio = total === 0 ? 0.5 : a / total;
  return Math.ceil(ratio * 7);
}

/**
 * Evolution milestones. The first five are the "turning points" drawn as
 * labelled guides on every chart; the rest are available behind a toggle.
 *
 * Raffle thresholds are quoted at the first `level` where the weight becomes
 * non-zero, e.g. medium biters need `-250 + 1.5 * level > 0`, so level 167.
 */
export interface Milestone {
  evo: number;
  label: string;
  detail: string;
  primary: boolean;
}

export const MILESTONES: Milestone[] = [
  {
    evo: 0.167,
    label: 'Medium biters',
    detail: 'raffle weight for medium units becomes non-zero at level 167',
    primary: true,
  },
  {
    evo: 0.501,
    label: 'Big biters',
    detail: 'raffle weight for big units becomes non-zero at level 501',
    primary: true,
  },
  {
    evo: 0.572,
    label: 'Small biters gone',
    detail: '1000 - 1.75 x level reaches 0 at level 572',
    primary: false,
  },
  {
    evo: 0.7,
    label: 'Boss units',
    detail: 'group size drops 300 -> 200 and half the wave budget goes to bosses',
    primary: true,
  },
  {
    evo: 0.901,
    label: 'Behemoths',
    detail: 'raffle weight for behemoth units becomes non-zero at level 901',
    primary: true,
  },
  {
    evo: 1,
    label: '100% evolution',
    detail: 'engine evo caps; health factor, damage modifier and threat scaling switch on',
    primary: true,
  },
  {
    evo: 1.3,
    label: 'Group size 100',
    detail: 'max group size 200 -> 100, boss attempts 5 -> 10',
    primary: false,
  },
  {
    evo: 2.3,
    label: 'Group size 75',
    detail: 'max group size 100 -> 75, boss attempts 10 -> 12',
    primary: false,
  },
  {
    evo: 3.25,
    label: 'Health cap 10x',
    detail: 'reanimation term saturates at 90%, so the health factor reaches 10x',
    primary: false,
  },
  {
    evo: 3.3,
    label: 'Doubling curve',
    detail: 'health factor switches to doubling every +70 evo points',
    primary: false,
  },
];
